import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useCurrentUser } from "@/lib/auth";
import { canViewHref } from "@/lib/acl";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";

export interface RequireAclProps {
  children: ReactNode;
  /** Klassischer ACL-Pfad, z. B. `/bestellungen.html`. */
  href: string;
}

/**
 * Rendert Kinder nur, wenn der Benutzer laut Server-ACL die Seite sehen darf.
 */
export function RequireAcl({ children, href }: RequireAclProps) {
  const { user, loading, error } = useCurrentUser();
  const groups = (user?.groups as string[] | undefined) ?? [];
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) {
      setAllowed(null);
      return;
    }
    let cancelled = false;
    void canViewHref(href, groups).then((ok) => {
      if (!cancelled) setAllowed(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [href, groups, user]);

  if (loading || (user && allowed === null)) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-label-secondary">
        <div className="flex items-center gap-2 text-sm">
          <Spinner />
          <span>Berechtigung wird geprüft …</span>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="mx-auto max-w-md py-10">
        <Alert variant="error" title="Sitzung konnte nicht geladen werden">
          {error || "Bitte erneut anmelden."}
        </Alert>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="mx-auto max-w-md py-10">
        <Alert variant="error" title="Keine Berechtigung">
          Diese Seite ist für deine Benutzergruppe nicht freigeschaltet.
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
